import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { BotContactMessage } from '@/types';
import { cn } from '@/utils/twMerge';
import { motion } from 'framer-motion';
import { Mail, Phone, Printer, Signal } from 'lucide-react';

type MessageContactProps = {
  message: BotContactMessage;
  messageId: string;
};

const MessageContact = ({ message, messageId }: MessageContactProps) => {
  const fullScreen = useChatStore(state => state.fullScreen);

  const { config } = useConfigStore();

  const isGpa = config.ui?.generalSpecificVariant === 'gpa';

  return (
    <motion.div
      layoutId={`${messageId}-contact`}
      key={`${messageId}`}
      className={cn(
        'flex w-full flex-col items-center gap-[12px] rounded-md border border-border px-[16px] py-[20px]',
        {
          'sm:flex-row sm:gap-[8px] sm:pl-[20px] sm:pr-[40px]': fullScreen,
        }
      )}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
    >
      {message.content.avatar && (
        <img
          src={message.content.avatar}
          alt={message.content.name}
          className="h-[96px] w-[96px] shrink-0 rounded-full object-cover object-center"
        />
      )}
      <div
        className={cn('flex shrink flex-col items-center text-center', {
          'sm:items-start sm:text-left': fullScreen,
        })}
      >
        <p className="break-words text-lg font-bold text-widget-text-secondary">
          {message.content.name}
        </p>
        {message.content.description && (
          <p className="break-words text-sm text-widget-text-tertiary">
            {message.content.description}
          </p>
        )}
      </div>
      <div
        className={cn('flex shrink-0 flex-col items-center', {
          'sm:items-start': fullScreen,
        })}
      >
        {message.content.phone && (
          <a
            className={cn('flex items-center text-[16px]', {
              'text-widget-accent-700 transition-colors hover:text-[#E95F47]': isGpa,
              'font-bold text-widget-accent-700': !isGpa,
            })}
            href={`tel:${message.content.phone}`}
          >
            {isGpa ? (
              <span className="text-[#E95F47]">m&nbsp;</span>
            ) : (
              <Phone className="mr-[8px] inline-flex size-[16px]" />
            )}
            <span
              className={cn({
                underline: isGpa,
              })}
            >
              {message.content.phone}
            </span>
          </a>
        )}
        {message.content.telephone && (
          <a
            className={cn('flex items-center text-[16px]', {
              'text-widget-accent-700 transition-colors hover:text-[#E95F47]': isGpa,
              'font-bold text-widget-accent-700': !isGpa,
            })}
            href={`tel:${message.content.telephone}`}
          >
            {isGpa ? (
              <span className="text-[#E95F47]">t&nbsp;</span>
            ) : (
              <Signal className="mr-[8px] inline-flex size-[16px]" />
            )}
            <span
              className={cn({
                underline: isGpa,
              })}
            >
              {message.content.telephone}
            </span>
          </a>
        )}
        {message.content.fax && (
          <a
            className={cn('flex items-center text-[16px]', {
              'text-widget-accent-700 transition-colors hover:text-[#E95F47]': isGpa,
              'font-bold text-widget-accent-700': !isGpa,
            })}
            href={`tel:${message.content.fax}`}
          >
            {isGpa ? (
              <span className="text-[#E95F47]">f&nbsp;</span>
            ) : (
              <Printer className="mr-[8px] inline-flex size-[16px]" />
            )}
            <span
              className={cn({
                underline: isGpa,
              })}
            >
              {message.content.fax}
            </span>
          </a>
        )}
        {message.content.email && (
          <a
            className={cn('flex items-center text-[16px]', {
              'text-widget-accent-700 transition-colors hover:text-[#E95F47]': isGpa,
              'font-bold text-widget-accent-700': !isGpa,
            })}
            href={`mailto:${message.content.email}`}
          >
            {isGpa ? (
              <span className="text-[#E95F47]">e-&nbsp;</span>
            ) : (
              <Mail className="mr-[8px] inline-flex size-[16px]" />
            )}
            <span
              className={cn({
                underline: isGpa,
              })}
            >
              {message.content.email}
            </span>
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default MessageContact;
